import styles from '@/components/app/upgrade/upgrade-view.module.css';
import { useCurrentUser } from '@/hooks/data/user';
import { SG_EVENT_NAMES } from '@/utils/tools/segment/eventNames';
import { trackEvent } from '@/utils/tools/segment/tracking';
import { Button, Card, Flex, message, Modal } from 'antd';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';

function PlanCard({ plan, isYearly, plans }) {
   const { user } = useCurrentUser();
   const [stripeLoading, setStripeLoading] = useState(false);

   async function startStripeCheckout() {
      setStripeLoading(true);
      try {
         const res = await fetch('/api/stripe/create-checkout-session', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
               planId: plan?._id,
               returnPath: '/account/subscription',
            }),
         });
         const data = await res.json();
         if (!res.ok || !data?.url) {
            throw new Error(data?.error || 'Could not start checkout');
         }
         trackEvent(SG_EVENT_NAMES.USER_UPGRADE_PLAN, {
            plan: plan?.name,
            price: plan?.price,
            provider: 'stripe',
         });
         window.location.assign(data.url);
      } catch (err) {
         message.error(err.message);
         setStripeLoading(false);
      }
   }
   const activeSubscription = user?.subscriptions.filter((subscription) => {
      const status = ['active', 'trailing', 'past_due', 'paused'];
      if (!status.includes(subscription.status)) return false;

      return subscription.subscriptionPlan?._id === plan._id;
   })[0];

   const isFreePlan = activeSubscription?.subscriptionPlan?.price === 0;

   const isPremiumPlan = plan?.price > 0;
   const userPremiumSubsciption = user?.subscriptions.filter((subscription) => subscription.paddleSubscriptionId)[0];
   const userPremiumPlan = userPremiumSubsciption?.subscriptionPlan;
   const userHasPremiumPlan = user?.subscriptions.filter((subscription) => {
      const status = ['active', 'trailing', 'past_due', 'paused'];
      if (!status.includes(subscription.status)) return false;
      return subscription.subscriptionPlan?.price > 0;
   }).length;

   async function requestUpgrade() {
      try {
         const userPremiumSubsciption = user.subscriptions.filter(
            (subscription) => subscription.paddleSubscriptionId
         )[0];
         const response = await fetch('/api/subscription/upgrade', {
            method: 'POST',
            body: JSON.stringify({
               paddle_plan_id: plan?.paddleId,
               subscription_id: userPremiumSubsciption?.paddleSubscriptionId,
               discount: -10,
            }),
            headers: {
               'Content-Type': 'application/json',
            },
         }).then((res) => res.json());
         if (response.error) {
            throw new Error(response.details);
         }
         // Track event in segment
         trackEvent(SG_EVENT_NAMES.USER_UPGRADE_PLAN, {
            plan: plan?.name,
            price: plan?.price,
         });

         return response;
      } catch (error) {
         return new Error(error);
      }
   }

   function UpgradePlan() {
      Modal.confirm({
         title: 'Are you sure you want to upgrade your plan?',
         content: 'You will be charged immediately',
         onOk() {
            return new Promise((resolve, reject) => {
               requestUpgrade()
                  .then(() => {
                     resolve();
                     Modal.success({
                        title: 'Success',
                        content: 'Your plan has been upgraded',
                     });
                  })
                  .catch((error) => {
                     reject();
                     Modal.error({
                        title: 'Error',
                        content: error.message,
                     });
                  });
            });
         },
      });
   }

   // Saving
   const monthlyProPlan = plans.find((p) => p.name === 'pro' && p.billingType === 'monthly');
   const monthlyProPlanPrice = monthlyProPlan.price || 0;
   const monthlyAdvancedPlan = plans.find((p) => p.name === 'advanced' && p.billingType === 'monthly');
   const monthlyAdvancedPlanPrice = monthlyAdvancedPlan.price || 0;

   const monthlyPrice =
      plan.billingType === 'monthly'
         ? plan.price
         : plan.name === 'pro'
         ? monthlyProPlanPrice
         : monthlyAdvancedPlanPrice;
   const yearlyPrice = isYearly ? plan.price : (plan?.price || 0) * 12;
   const saving = isYearly ? Math.max(0, (monthlyPrice || 0) * 12 - (yearlyPrice || 0)) : 0;

   return (
      <Card
         style={{
            padding: '8px',
            width: '100%',
            borderRadius: '8px',
            position: 'relative',
            background: '#F4F6FA',
            boxShadow: '0 0 50px rgb(192, 212, 250)',
         }}
         key={plan?.name}
      >
         {plan?.name === 'pro' && (
            <div
               style={{
                  position: 'absolute',
                  top: '20px',
                  right: '0%',
                  zIndex: 1,
               }}
            >
               <Image
                  src="/images/upgrade/best-value.svg"
                  alt="best_value"
                  width={120}
                  height={140}
                  style={{ maxWidth: '100%', height: 'auto' }}
               />
            </div>
         )}

         <Flex vertical gap={16}>
            {/* plan Title */}
            <h1 className={plan?.name === 'pro' ? styles.proHeading : styles.advancedHeading}>
               {capitalizeWord(plan?.name)}
            </h1>
            {/* Money Amount */}
            <Flex gap={8} align="center" justify="space-between">
               <h1 className={styles.cardPrice}>
                  {isYearly ? `$${(plan?.price / 12)?.toFixed(0)}` : `$${plan?.price?.toFixed(0)}`}
               </h1>

               {isYearly && (
                  <div>
                     <p
                        className={isYearly ? styles['active-payment'] : ''}
                        style={{ fontWeight: '500', color: '#8E8E9E', fontSize: '16px' }}
                     >
                        Billed annually
                     </p>
                     <p style={{ color: '#8E8E9E', fontWeight: '400', fontSize: '16px' }}>
                        12mo . ${plan?.price?.toFixed(0)}
                     </p>
                  </div>
               )}
            </Flex>

            {/* Upgrade Button variations */}
            {isFreePlan && userHasPremiumPlan ? (
               //FIXME - add cancel subscription to plan plan
               <Button color="primary" fullWidth>
                  Cancel subscription
               </Button>
            ) : null}

            {isPremiumPlan && !activeSubscription && !userHasPremiumPlan ? (
               plan?.stripePriceId ? (
                  <Button
                     type="primary"
                     fullWidth
                     style={{ width: '100%' }}
                     loading={stripeLoading}
                     onClick={startStripeCheckout}
                  >
                     Upgrade now
                  </Button>
               ) : (
                  <Link href={`/checkout?planId=${plan?.paddleId}`}>
                     <Button type="primary" fullWidth style={{ width: '100%' }}>
                        Upgrade now{' '}
                     </Button>
                  </Link>
               )
            ) : null}

            {isYearly && (
               <p
                  style={{ textAlign: 'center', color: '#8E8E9E', fontSize: '14px' }}
               >{`Save $${saving} with annual billing`}</p>
            )}

            {isPremiumPlan && !activeSubscription && userHasPremiumPlan && plan?.price > userPremiumPlan?.price ? (
               <Button variant="contained" color="primary" onClick={UpgradePlan}>
                  Upgrade plan
               </Button>
            ) : null}

            {isPremiumPlan && !activeSubscription && userHasPremiumPlan && plan?.price < userPremiumPlan?.price ? (
               <Button color="primary" onClick={UpgradePlan}>
                  Downgrade plan
               </Button>
            ) : null}

            {isPremiumPlan && activeSubscription ? (
               <Link href={'/account/subscription'}>
                  <Button color="primary">Manage subscription</Button>
               </Link>
            ) : null}
            {/* <Button type="primary" onClick={UpgradePlan}>
                Upgrade now
             </Button> */}
         </Flex>
      </Card>
   );
}

export default PlanCard;

function capitalizeWord(word) {
   return word[0].toUpperCase() + word.slice(1);
}
